import ErrorBoundary from '@components/ErrorBoundary';
import { Spinner } from '@components/spinner';
import { PermissionClientAdminRead } from '@constants/Auth';
import { MainLayout, PublicLayout } from '@layouts';
import { Layout } from '@layouts/models';
import { SafeAny } from '@models/common';
import { Routes } from '@models/route';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { PrivateRoute, SignedOutPage } from '@swyft/auth';
import { MapProvider } from '@swyft/map';
import { FeatureNames, MainLayoutProvider } from '@swyft/ui';
import { useAppContext } from '@utils/appState';
import { useFeature } from '@utils/config';
import { history } from '@utils/history';
import { flattenRoutes } from '@utils/routes';
import * as React from 'react';
import { Redirect, Route as RouteComponent, RouteProps, Router, Switch } from 'react-router-dom';
import { QueryParamProvider } from 'use-query-params';
import { AnalyticRoutes } from './Analytic';
import { ContactRoutes } from './Contact';
import { ErrorRoutes } from './Error';
import { LeadRoutes } from './Lead';
import { ProductRoutes } from './Product';
import { QuestionnaireRoutes } from './Questionnaire';
import { PreviewRoutes } from './QuestionnairePreview';
import { QuoteRoutes } from './Quote';
import { SettingRoutes } from './Settings';

const publicRoutes: Routes = flattenRoutes([...ErrorRoutes]);
const fullPageRoutes: Routes = flattenRoutes([...PreviewRoutes]);

const AppRoute = ({
  component: Component,
  layout: Layout = MainLayout,
  ...rest
}: RouteProps & {
  component: SafeAny;
  layout?: Layout;
}): React.ReactElement => (
  <RouteComponent
    {...rest}
    render={(props) => (
      <Layout>
        <React.Suspense fallback={<Spinner />}>
          <Component {...props} />
        </React.Suspense>
      </Layout>
    )}
  />
);

export const AppRoutes = (): React.ReactElement => {
  const { featureConfig: mapFeature } = useFeature(FeatureNames.Maps);
  const { enabled: contactFeatureEnabled } = useFeature(FeatureNames.Contact);
  const { enabled: quoteFeatureEnabled } = useFeature(FeatureNames.Quote);
  const { enabled: analyticFeatureEnabled } = useFeature(FeatureNames.Analytics);
  const {
    state: { stripeApiKey }
  } = useAppContext();
  const googleMapKey = mapFeature?.GoogleMapsApiKey || '';

  const [isLoadStripe, setIsLoadStripe] = React.useState<boolean>(false);

  const stripePromise = React.useMemo(() => {
    if (stripeApiKey && !isLoadStripe) {
      setIsLoadStripe(true);
      return loadStripe(stripeApiKey);
    }
    return null;
  }, [stripeApiKey]);

  let routes = [...LeadRoutes, ...ProductRoutes, ...QuestionnaireRoutes, ...SettingRoutes];
  if (contactFeatureEnabled) {
    routes = [...routes, ...ContactRoutes];
  }
  if (quoteFeatureEnabled) {
    routes = [...routes, ...QuoteRoutes];
  }
  if (analyticFeatureEnabled) {
    routes = [...routes, ...AnalyticRoutes];
  }
  const mainRoutes: Routes = flattenRoutes(routes);

  return (
    <Elements stripe={stripePromise}>
      <MapProvider googleMapKey={googleMapKey}>
        <Router history={history}>
          <QueryParamProvider ReactRouterRoute={RouteComponent}>
            <MainLayoutProvider>
              <Switch>
                <RouteComponent
                  exact
                  path='/admin/signout-redirect'
                  component={() => <SignedOutPage name='Client Portal' redirectPath='/' />}
                />
                <Redirect from='(/admin/|/admin)' exact to='/admin/leads' />
                {mainRoutes
                  .filter(({ component }) => component)
                  .map(({ path, component: Component }) => (
                    <PrivateRoute
                      key={path}
                      path={path}
                      exact
                      permission={PermissionClientAdminRead}
                      render={(props) => (
                        <ErrorBoundary>
                          <MainLayout>
                            <React.Suspense fallback={<Spinner />}>
                              <Component {...props} />
                            </React.Suspense>
                          </MainLayout>
                        </ErrorBoundary>
                      )}
                    />
                  ))}
                {publicRoutes
                  .filter((r) => r.component)
                  .map((r) => (
                    <AppRoute exact layout={PublicLayout} component={r.component} path={r.path} key={r.path} />
                  ))}
                {fullPageRoutes
                  .filter(({ component }) => component)
                  .map(({ path, component: Component }) => (
                    <PrivateRoute
                      key={path}
                      path={path}
                      exact
                      permission={PermissionClientAdminRead}
                      render={(props) => (
                        <React.Suspense fallback={<Spinner />}>
                          <Component {...props} />
                        </React.Suspense>
                      )}
                    />
                  ))}
              </Switch>
            </MainLayoutProvider>
          </QueryParamProvider>
        </Router>
      </MapProvider>
    </Elements>
  );
};
